import React, { useMemo, useRef } from 'react';
import Flickity from 'react-flickity-component';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { PageSlice, SliceInnerButtonSliderDetails, SliceInnerButtonSliderItem } from '@contracts';
import CarouselChevron from '@images/CarouselChevron';
import Carousel from '../Carousel';
import Colors from '../Colors';
import { TextVariant, Typography } from './Typography';

interface GridProps {
  slice: PageSlice;
}

const carouselOptions = {
  initialIndex: 0,
  groupCells: 1,
  prevNextButtons: false,
  contain: true,
  cellAlign: 'left',
  watchCSS: false,
  draggable: true
};

const InnerButtonSlider: React.FC<GridProps> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceInnerButtonSliderDetails;
  const sliceItems = slice.items as SliceInnerButtonSliderItem[];
  const carouselRef = useRef<Flickity | null>(null);

  const carouselNext = () => {
    const flickity = carouselRef?.current;

    if (flickity) {
      flickity.next();
    }
  };

  const carouselPrevious = () => {
    const flickity = carouselRef?.current;

    if (flickity) {
      flickity.previous();
    }
  };

  return (
    <Wrapper>
      <InnerWrap>
        {useMemo(
          () => (
            <Carousel options={carouselOptions} ref={carouselRef}>
              {sliceItems.map((item, index) => (
                <SlideItem key={index} $background={sliceDetails?.background_text}>
                  <SlideImageWrap>
                    <GatsbyImage image={item.image.gatsbyImageData} alt={item.image.alt || ''} />
                  </SlideImageWrap>
                  <SlideInfoWrap>
                    {item?.overline ? (
                      <OverlineWrap>
                        <Typography $variant={TextVariant.UpperText4}>{item.overline}</Typography>
                      </OverlineWrap>
                    ) : null}
                    {item?.heading ? (
                      <HeadingWrap>
                        <Typography $variant={TextVariant.Heading3} $mobileVariant={TextVariant.Heading6} $breakpoint={1350} as="h4">
                          {item.heading.text}
                        </Typography>
                      </HeadingWrap>
                    ) : null}
                    {item?.bodycopy ? (
                      <BodyWrap>
                        <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
                          {item.bodycopy.text}
                        </Typography>
                      </BodyWrap>
                    ) : null}
                    <CarouselControls>
                      <CarouselPrevious disabled={index === 0} onClick={carouselPrevious} aria-label="Carousel previous page">
                        <CarouselChevron />
                      </CarouselPrevious>
                      <CarouselNext disabled={index === sliceItems.length - 1} onClick={carouselNext} aria-label="Carousel next page">
                        <CarouselChevron />
                      </CarouselNext>
                    </CarouselControls>
                  </SlideInfoWrap>
                </SlideItem>
              ))}
            </Carousel>
          ),
          []
        )}
      </InnerWrap>
    </Wrapper>
  );
};

export default InnerButtonSlider;

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

const InnerWrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;

  .flickity-viewport {
    overflow: visible !important;
  }
`;

const SlideItem = styled.div<{ $background?: string }>`
  background: ${(props) => props.$background || Colors.babyBlue};
  width: 100%;
  margin: 0 auto;
  display: flex;
  opacity: 0.5;

  &.is-selected {
    opacity: 1;
  }

  > div {
    width: 50%;
  }

  @media (max-width: 900px) {
    flex-direction: column;

    > div {
      width: 100%;
    }
  }
`;

const SlideImageWrap = styled.div`
  img {
    display: block;
  }
`;

const SlideInfoWrap = styled.div`
  display: flex;
  padding: 48px 72px 80px;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 28px 24px 56px;
  }
`;

const OverlineWrap = styled.div``;

const HeadingWrap = styled.div`
  margin-top: 12px;

  @media (max-width: 768px) {
    margin-top: 8px;
  }
`;

const BodyWrap = styled.div`
  margin-top: 24px;

  @media (max-width: 768px) {
    margin-top: 16px;
  }
`;

const CarouselControls = styled.div`
  display: flex;
  gap: 16px;
  position: absolute;
  bottom: 48px;

  @media (max-width: 900px) {
    margin-top: 24px;
    position: relative;
    bottom: 0;
  }
`;

const CarouselNext = styled.button`
  background: none;
  border: 2px solid ${Colors.paleGold};
  height: 32px;
  width: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  svg {
    margin-left: 2.5px;
    height: 16px;
    width: 16px;

    path {
      fill: ${Colors.navyBlue};
    }
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const CarouselPrevious = styled(CarouselNext)`
  transform: rotate(180deg);
`;
